import { FC } from 'react'
import { useMutation, useQuery } from '@sequencehq/api'
import { SETTINGS_STALE_TIME } from '../settings.constants'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { NotificationPolicySettings } from 'SettingsV2/components/notifications/NotificationsPolicySettings'
import { useNavigate, useParams } from 'react-router-dom'
import { useNotifications } from '@sequencehq/core-components'
import { NotificationPolicyValidFormData } from 'SettingsV2/components/notifications/nofitications.utils'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

type NotificationType = Dashboardv99990101Api.Notifications.NotificationType

export const NotificationsPolicySettingsPage: FC = () => {
  const { id } = useParams<{ id: string }>()

  const navigate = useNavigate()

  const { data, isError, isLoading } = useQuery(
    dashboard20240730Client.getNotificationPolicy,
    { id: id ?? '' },
    {
      enabled: Boolean(id),
      staleTime: SETTINGS_STALE_TIME
    }
  )

  const { displayNotification } = useNotifications()

  const postNotificationPolicyRecipients = useMutation(
    dashboard20240730Client.postNotificationPolicyRecipients
  )
  const deleteNotificationPolicyRecipients = useMutation(
    dashboard20240730Client.deleteNotificationPolicyRecipients
  )

  const postNotificationPolicyType = useMutation(
    dashboard20240730Client.postNotificationPolicyType
  )

  const deleteNotificationPolicyType = useMutation(
    dashboard20240730Client.deleteNotificationPolicyType
  )

  const handleOnSubmit = async (formData: NotificationPolicyValidFormData) => {
    if (!data) return

    const notificationTypes = data.notificationTypes ?? []

    const recipientsToAdd = formData.recipients.filter(
      recipient => !data.recipients.includes(recipient)
    )

    const recipientsToRemove = data.recipients.filter(
      (recipient: string) => !formData.recipients.includes(recipient)
    )

    const typesToAdd = formData.notificationTypes.filter(
      type => !notificationTypes.includes(type)
    )

    const typesToRemove = notificationTypes.filter(
      (type: NotificationType) => !formData.notificationTypes.includes(type)
    )

    const promises = []

    if (recipientsToAdd.length) {
      promises.push(
        postNotificationPolicyRecipients.mutateAsync({
          id: data.id,
          recipients: recipientsToAdd
        })
      )
    }

    if (typesToAdd.length) {
      promises.push(
        postNotificationPolicyType.mutateAsync({
          id: data.id,
          notificationTypes: typesToAdd
        })
      )
    }

    typesToRemove.map(notificationType => {
      promises.push(
        deleteNotificationPolicyType.mutateAsync({
          id: data.id,
          notificationType
        })
      )
    })

    if (!promises.length) return

    const res = await Promise.allSettled(promises)

    const recipientsToRemoveRes = await Promise.allSettled(
      recipientsToRemove.map(recipient => {
        deleteNotificationPolicyRecipients.mutateAsync({
          id: data.id,
          recipient
        })
      })
    )

    if (
      [...recipientsToRemoveRes, ...res].every(r => r.status === 'fulfilled')
    ) {
      displayNotification(`Notification policy updated`, {
        type: 'success'
      })
      navigate('/settings/notifications')
    } else {
      displayNotification(`Notification policy failed to update`, {
        type: 'error'
      })
    }
  }

  const isPending =
    postNotificationPolicyRecipients.isPending ||
    deleteNotificationPolicyRecipients.isPending ||
    postNotificationPolicyType.isPending ||
    deleteNotificationPolicyType.isPending

  return (
    <NotificationPolicySettings
      channel="EMAIL"
      isLoading={isLoading}
      data={data}
      header={data?.name ?? ''}
      isPending={isPending}
      isError={isError}
      onSubmit={handleOnSubmit}
      disableNameField
    />
  )
}
